import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionCard,
  ActionCardBody,
  ActionCardMeta,
  ActionCardSection,
  ActionCardSummary,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import '@sola/locales/formatters.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';
import { mapFundCodeToCommitmentType } from '../../organisms/giving-history/giving-history.logic.js';
import { GivingItemWithCommitment } from '../../utils/giving-history.js';
import { getHistoryItemData } from './giving-history-card.logic.js';

export type GivingHistoryCardProps = {
  givingItem: GivingItemWithCommitment;
};

const getIcon = (givingItem: GivingItemWithCommitment) => {
  if (givingItem.commitment) {
    return <CommitmentAvatar responsive commitment={givingItem.commitment} />;
  }

  // Some giving items have enought data to craft an icon even without a commitment
  return (
    <CommitmentAvatar
      responsive
      commitment={{
        id: '',
        commitmentType: mapFundCodeToCommitmentType(givingItem.fundCode),
        fund: {
          code: givingItem.fundCode,
          icon: givingItem.fundIcon,
        },
      }}
    />
  );
};

const useComponentData = (args: GivingHistoryCardProps & { t: TFunction }) => {
  const { givingItem, t } = args;

  const { title, label, rate } = getHistoryItemData(givingItem, t);

  const icon = getIcon(givingItem);

  return {
    icon,
    title,
    label,
    rate,
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const GivingHistoryCard: React.FC<GivingHistoryCardProps> = (props) => {
  const { t } = useTranslation(['my', 'common']);

  const cd = useComponentData({ t, ...props });

  const rate =
    typeof cd.rate === 'string'
      ? cd.rate
      : t('numbers.currencyCustom', {
          amount: cd.rate,
          useNegativeParens: true,
        });

  return (
    <ActionCard>
      <ActionCardSection>
        <ActionCardBody>
          <ActionCardSummary
            icon={cd.icon}
            title={cd.title}
            subTitle={cd.label}
          />
          <ActionCardMeta text={rate} />
        </ActionCardBody>
      </ActionCardSection>
    </ActionCard>
  );
};
